import { useTranslation } from 'react-i18next';

import { ScoreBadge } from '@/components/atoms/ScoreBadge';
import { useDailyConsolidated } from '@/services/Reports';

import { REPORT_TABLE_COLUMN_KEYS } from './Quality.constants';
import { styles } from './Quality.styles';
import type {
	ConsolidatedCellProps,
	ConsolidatedListProps,
	ConsolidatedSummaryProps,
	DailyReportRowProps,
	DailyReportsTableProps,
} from './Quality.types';
import { formatAverage, formatReportDay, reportKeyOf } from './Quality.utils';

const ConsolidatedList = ({ title, items }: ConsolidatedListProps) => {
	if (items.length === 0) return null;

	return (
		<div>
			<p className={styles.consolidatedListTitle}>{title}</p>
			<ul className={styles.consolidatedList}>
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>
	);
};

const ConsolidatedSummary = ({ summary }: ConsolidatedSummaryProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });

	return (
		<section className={styles.consolidated}>
			<p className={styles.consolidatedTitle}>{t('CONSOLIDATED_TITLE')}</p>
			<p className={styles.consolidatedBody}>{summary.summary}</p>
			<div className={styles.consolidatedGrid}>
				<ConsolidatedList title={t('CONSOLIDATED_WENT_WELL')} items={summary.wentWell} />
				<ConsolidatedList title={t('CONSOLIDATED_NEEDS_REVIEW')} items={summary.needsReview} />
				<ConsolidatedList title={t('CONSOLIDATED_ACTIONS')} items={summary.recommendedActions} />
			</div>
		</section>
	);
};

/** Mounts only when a row is expanded, so each summary (one LLM call) is fetched on demand. */
const ConsolidatedCell = ({ moduleName, day }: ConsolidatedCellProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });
	const { data, isLoading, isError } = useDailyConsolidated(moduleName, day);
	const summary = data?.items.find((r) => r.moduleName === moduleName && r.day === day)?.consolidatedSummary;

	if (isLoading) return <p className={styles.consolidatedNotice}>{t('LOADING_CONSOLIDATED')}</p>;
	if (isError) return <p className={styles.consolidatedError}>{t('ERROR_CONSOLIDATED')}</p>;
	if (!summary) return <p className={styles.consolidatedNotice}>{t('NO_CONSOLIDATED')}</p>;
	return <ConsolidatedSummary summary={summary} />;
};

const DailyReportRow = ({ report, isExpanded, onToggle }: DailyReportRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });

	return (
		<>
			<tr className={styles.reportRow}>
				<td className={styles.reportDayCell}>{formatReportDay(report.day)}</td>
				<td className={styles.reportNumCell}>
					{report.llmPassing}/{report.llmTotal}
				</td>
				<td className={styles.reportCell}>
					<ScoreBadge score={report.llmScore ?? null} size="sm" />
				</td>
				<td className={styles.reportNumCell}>
					{report.externalPassing}/{report.externalTotal}
				</td>
				<td className={styles.reportCell}>
					<ScoreBadge score={report.externalScore ?? null} size="sm" />
				</td>
				<td className={styles.reportNumCell}>{formatAverage(report.externalAverage)}</td>
				<td className={styles.reportNumCell}>
					{report.closedTotal}/{report.conversationsTotal}
				</td>
				<td className={styles.reportNumCell}>
					{report.handoffTotal}/{report.conversationsTotal}
				</td>
				<td className={styles.reportCell}>
					<button
						type="button"
						aria-expanded={isExpanded}
						onClick={() => onToggle(reportKeyOf(report))}
						className={styles.reportToggle}
					>
						{isExpanded ? t('HIDE_CONSOLIDATED') : t('SHOW_CONSOLIDATED')}
					</button>
				</td>
			</tr>
			{isExpanded && (
				<tr className={styles.reportExpandedRow}>
					<td colSpan={REPORT_TABLE_COLUMN_KEYS.length} className={styles.reportExpandedCell}>
						<ConsolidatedCell moduleName={report.moduleName} day={report.day} />
					</td>
				</tr>
			)}
		</>
	);
};

export const DailyReportsTable = ({ rows, expandedKey, onToggle }: DailyReportsTableProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });

	return (
		<section className={styles.reportTableWrap}>
			<table className={styles.reportTable}>
				<thead className={styles.reportTableHead}>
					<tr>
						{REPORT_TABLE_COLUMN_KEYS.map((key) => (
							<th key={key} scope="col" className={styles.reportTh}>
								{t(key)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((report) => (
						<DailyReportRow
							key={reportKeyOf(report)}
							report={report}
							isExpanded={expandedKey === reportKeyOf(report)}
							onToggle={onToggle}
						/>
					))}
				</tbody>
			</table>
		</section>
	);
};
